import React from "react";
import PropTypes from "prop-types";
import UniqueId from "../../utils/UniqueId";
import classnames from "classnames";
import { TAB, SPACE, ENTER, ESCAPE, DOWN, UP } from "../../constants/Keys";

const BLUR_HIDE_MENU_TIME = 250; // ms

class Dropdown extends React.Component {
    constructor(...args) {
        super(...args);

        this.state = {
            expanded: false,
            selectedIndex: this.props.selectedIndex,
        };

        this._componentId = UniqueId.generate("dropdown");
        this._titleEl = null;
        this._menuEl = null;
        this._listEl = [];
        this._parentEl = null;
        this._blurHideTimeoutId = null;

        this._toggleDropdown = this._toggleDropdown.bind(this);
        this._handleTitleKeyDown = this._handleTitleKeyDown.bind(this);
        this._handleMenuKeyDown = this._handleMenuKeyDown.bind(this);
        this._handleMenuBlur = this._handleMenuBlur.bind(this);
    }

    render() {
        const { baseClassName, dropdownTitle, dropdownItems, dropdownLabel } =
            this.props;
        const { expanded, selectedIndex } = this.state;
        const titleId = `${this._componentId}-title`;
        const menuId = `${this._componentId}-menu`;

        return (
            <div
                className={classnames(baseClassName, {
                    [`${baseClassName}--expanded`]: expanded,
                })}
                ref={(el) => (this._parentEl = el)}
            >
                <div
                    role="button"
                    aria-haspopup="menu"
                    tabIndex="0"
                    onClick={this._toggleDropdown}
                    className={`${baseClassName}__title`}
                    onKeyDown={this._handleTitleKeyDown}
                    aria-expanded={expanded}
                    id={titleId}
                    aria-controls={menuId}
                    aria-label={dropdownLabel}
                    ref={(el) => (this._titleEl = el)}
                >
                    {dropdownTitle}
                </div>

                <ul
                    id={menuId}
                    aria-labelledby={titleId}
                    role="menu"
                    tabIndex="-1"
                    className={`${baseClassName}__items`}
                    ref={(el) => (this._menuEl = el)}
                    onKeyDown={this._handleMenuKeyDown}
                    onBlur={(e) => this._handleMenuBlur(e, this._parentEl)}
                >
                    {dropdownItems.map((dropdownItem, index) => (
                        <li
                            key={index}
                            role="menuitem"
                            onMouseEnter={() => this._onMouseEnter(index)}
                            ref={(el) => (this._listEl[index] = el)}
                            tabIndex="-1"
                            className={classnames(`${baseClassName}__item`, {
                                [`${baseClassName}__item--selected`]:
                                    selectedIndex === index,
                            })}
                        >
                            {dropdownItem}
                        </li>
                    ))}
                </ul>
            </div>
        );
    }

    componentWillUnmount() {
        this._clearBlurTimeout();
    }

    _toggleDropdown() {
        if (this.state.expanded) {
            this._hideMenu();
        } else {
            this._showMenu();
        }
    }

    _showMenu() {
        this._setMenuState(true);
        this._menuEl.focus();
        this.props.onShown();
    }

    _hideMenu() {
        this.setState({ selectedIndex: -1 });
        this._setMenuState(false);
    }

    _setMenuState(expanded) {
        this._clearBlurTimeout();
        this.setState({ expanded });
    }

    _clearBlurTimeout() {
        if (this._blurHideTimeoutId !== null) {
            window.clearTimeout(this._blurHideTimeoutId);
            this._blurHideTimeoutId = null;
        }
    }

    _handleMenuBlur(e, parent) {
        let target = e.relatedTarget;
        if (target === null) {
            // IE11 uses activeElement instead of relatedTarget for the new focus element after a blur event
            target = document.activeElement;
        }
        // Blur events don't bubble up to the parent element. When focus changes to the child elements of the dropdown,
        // the menu should not be hidden. ParentEl includes both the dropdown link and its child list elements.
        const hasFocus = parent && parent.contains(target);

        this._blurHideTimeoutId = window.setTimeout(() => {
            if (!hasFocus) {
                this._blurHideTimeoutId = null;
                this._hideMenu();
            }
        }, BLUR_HIDE_MENU_TIME);
    }

    _handleTitleKeyDown(event) {
        const { key } = event;
        if (key === ENTER || key === SPACE || key === UP || key === DOWN) {
            this._showMenu();
            event.preventDefault();
        }
    }

    _handleMenuKeyDown(event) {
        switch (event.key) {
            case ENTER:
            case SPACE:
                this._chooseMenuItem();
                event.preventDefault();
                break;
            case UP:
                this._selectPrevious();
                event.preventDefault();
                break;
            case DOWN:
                this._selectNext();
                event.preventDefault();
                break;
            case ESCAPE:
                this._hideMenu();
                this._titleEl.focus();
                break;
            case TAB:
                event.preventDefault();
                break;
        }
    }

    _chooseMenuItem() {
        const { selectedIndex } = this.state;
        if (selectedIndex >= 0) {
            this.props.onItemChosen(selectedIndex);
        }
    }

    _focusListItem(index) {
        this._listEl[index].focus();
    }

    _selectNext() {
        const { selectedIndex } = this.state;
        if (selectedIndex < this.props.dropdownItems.length - 1) {
            this.setState({ selectedIndex: selectedIndex + 1 });
            this._focusListItem(selectedIndex + 1);
        }
    }

    _selectPrevious() {
        const { selectedIndex } = this.state;
        if (selectedIndex > 0) {
            this.setState({ selectedIndex: selectedIndex - 1 });
            this._focusListItem(selectedIndex - 1);
        }
    }

    _onMouseEnter(index) {
        this.setState({ selectedIndex: index });
    }
}

Dropdown.defaultProps = {
    baseClassName: "dropdown",
    onShown: () => {},
    onItemChosen: () => {},
    selectedIndex: -1,
};

Dropdown.propTypes = {
    dropdownTitle: PropTypes.any,
    dropdownItems: PropTypes.array,
    dropdownLabel: PropTypes.string,
    baseClassName: PropTypes.string,
    onShown: PropTypes.func,
    onItemChosen: PropTypes.func,
    selectedIndex: PropTypes.number,
};

export default Dropdown;
